import { useDeviceSetting } from '@battleops/client/src/data/useDeviceSetting';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { deptNeon, deptColor, formatDurationShort, moneyShort, burnView, isRemoteUri, PHOTO_TAG_LABEL, localToday, targetFromText, FIELD_RANGES } from '@battleops/shared';
import { useDeptGoals, useFieldDashboard } from '@battleops/client/src/data/useDeptGoals';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { useNow, useTaskModal } from '../../hooks';
import Kpi from '../Kpi';
import { StackedBars, Sparkline, SplitBar, Legend, SERIES, OTHER } from '../charts';
import { distinctColors } from '../../lib/colors';
import { ScoreHero } from './ScoreCard';
import { Picker } from './PinnedProjects';
import { TONE } from './DeptParts';
import { weekOf } from '../DeskPanel';
import Icon from '../Icon';
import s from '../../pages/Home.module.css';
import d from './DevDashboard.module.css';
import f from './FieldDashboard.module.css';
import p from './PinnedProjects.module.css';

// Field's Home (Lj, Oct 11: "the Field Home — same quality bar as the Dev Home"): the score hero, then on site now
// (or today's jobs to Start) beside four KPI tiles, the 14 days by job, Field work vs other work, earned per hour, the
// efficiency bullets, the jobs pinned to Home, photos, receipts + store runs, callbacks + margin, Working on (Field's
// plan) and Needs you. Every number from shared fieldDashboard — the Field goals' own counts — with the running timer
// counted live; each card says what it counted.

const H = 3600000;
const hrs = (ms) => (ms >= H ? `${(ms / H).toFixed(1)}h` : formatDurationShort(ms));
const shortDay = (date) => new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
const dayLetter = (date) => new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'narrow' });
const weekLabel = (from) => `Week of ${new Date(`${from}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
const perH = (v) => (v == null ? '—' : `$${Math.round(v)}/h`);
const KIND_COLOR = (neon) => ({ 'Job tasks': neon[1], 'Setup + cleanup': SERIES[2], 'Runs + travel': SERIES[1], 'Other quick tasks': SERIES[4], 'Other work': OTHER });
const targetSource = (from, pct) => (from === 'jobs' ? 'from your jobs’ bids and rates' : from === 'rate' ? `from your BattleBooks rate${pct && pct !== 100 ? ` × ${pct}%` : ''}` : from === 'goal' ? 'your goal' : 'until a job has a bid or rate');

/** "✓ what it counted · ⊖ what it left out" — one quiet line under a card. */
function Counted({ counted, leftOut = [] }) {
  if (!counted && !leftOut.length) return null;
  return (
    <div className={f.counted}>
      {counted ? <span><Icon name="check-circle" size={12} style={{ color: TONE.met }} />{counted}</span> : null}
      {leftOut.slice(0, 2).map((n) => <span key={n} className={f.left}><Icon name="minus-circle" size={12} />{n}</span>)}
    </div>
  );
}

/** Seven (or n) thin bars, today lit. */
function MiniBars({ points, color, height = 36, format = (v) => String(v) }) {
  const max = Math.max(1e-9, ...points.map((x) => x.value || 0));
  return (
    <div className={f.mini} style={{ height }} role="img" aria-label={points.map((x) => `${x.label}: ${format(x.value || 0)}`).join(', ')}>
      {points.map((x, i) => (
        <span key={x.key} title={`${x.label}: ${format(x.value || 0)}`} data-future={x.future || undefined}
          style={{ height: x.value ? `${Math.max(8, (x.value / max) * 100)}%` : 3, background: x.value ? color : 'var(--c-border-strong)', opacity: x.value ? (i === points.length - 1 || x.lit ? 1 : 0.6) : 0.6 }} />
      ))}
    </div>
  );
}

/** Work done vs hours used — the hours bar capped at the estimate with the overflow red (never "5920%"). */
function PaceBars({ burn, neon }) {
  const v = burnView(burn);
  if (!v) return <div className={d.quiet}>No estimates on this job’s Field tasks yet — the pace line shows once they have some.</div>;
  const toneOf = { far: TONE.over, over: TONE.close, on: TONE.met, none: 'var(--c-text-tertiary)' };
  return (
    <div className={f.pace} role="img" aria-label={`Work done ${v.donePct}%. Hours used: ${v.usedText}. ${v.paceText}`}>
      <div className={f.paceHead}><span>Work done</span><b>{v.donePct}%</b></div>
      <div className={f.track}><span style={{ width: `${v.donePct}%`, background: `linear-gradient(90deg, ${neon[0]}, ${neon[1]})` }} /></div>
      <div className={f.paceHead}><span>Hours used</span><b style={v.over ? { color: TONE.over } : null}>{v.usedText}</b></div>
      <div className={f.track}>
        {v.over ? <>
          <span style={{ width: `${v.estPct}%`, background: '#9FB6CC', borderRadius: '5px 0 0 5px' }} />
          <span style={{ width: `${v.overPct}%`, background: TONE.over, borderRadius: '0 5px 5px 0', marginLeft: 2 }} />
        </> : <span style={{ width: `${v.fillPct}%`, background: v.paceTone === 'over' ? TONE.close : '#9FB6CC' }} />}
      </div>
      <span className={f.paceWord} style={{ color: toneOf[v.paceTone] }}>{v.paceText}</span>
    </div>
  );
}

/** On site now — or, not on site, today's jobs to Start (and what's running elsewhere). */
function OnSite({ dept, x, neon, maxJobs = 4 }) {
  const navigate = useNavigate();
  const details = useTaskModal();
  const { clockedIn, activeTaskId, startQuickTask, clockIn } = useTasks();
  const now = useNow(1000);
  const [err, setErr] = useState(null);
  const e = x.engine;
  const site = e.site;
  const today = localToday();
  const tap = (q) => { setErr(null); try { startQuickTask(dept.id, q.id); } catch (er) { setErr(String(er?.message || er)); } };
  return (
    <section className={`${s.hero} ${f.site}`} style={{ '--pc': neon[1] }}>
      {e.elsewhere ? (
        <button type="button" className={f.elsewhere} onClick={() => (e.elsewhere.task ? details.open(e.elsewhere.task.id) : e.elsewhere.project ? navigate(`/projects/${e.elsewhere.project.id}`) : null)}>
          <span className={s.pulse} />Clocked on {e.elsewhere.project?.name || e.elsewhere.task?.title || 'another job'} · {e.elsewhere.dept?.name || 'another department'}
        </button>
      ) : null}
      <div className={s.kicker}>{e.onSite ? <><span className={s.pulse} style={{ background: neon[1], boxShadow: `0 0 8px ${neon[1]}` }} />On site now</> : `Today’s jobs · ${dept.name}`}</div>
      {e.onSite ? (
        <>
          <div className={f.siteHead}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <button type="button" className={s.heroTitle} onClick={() => navigate(`/projects/${site.project.id}`)}>{site.project.icon ? `${site.project.icon} ` : ''}{site.project.name}</button>
              <div className={s.heroMeta}>
                {site.running ? <button type="button" className={f.runLine} onClick={() => details.open(site.running.id)}><span className="dot" style={{ background: neon[1] }} />{site.running.title}</button> : <span>No task running — pick one below</span>}
                {site.project.address ? <span><Icon name="map-pin" size={13} />{site.project.address}</span> : null}
              </div>
            </div>
            {clockedIn?.startedAt ? <div className={f.clock}><b className="num">{formatDurationShort(now - clockedIn.startedAt)}</b><span>on the clock</span></div> : null}
          </div>
          <PaceBars burn={site.burn} neon={neon} />
          {e.quick.length ? (
            <div className={f.quick}>
              {e.quick.slice(0, 6).map((q) => (
                <button key={q.id} type="button" className={f.qt} onClick={() => tap(q)} title={`Start ${q.title} — clocks onto it`}>
                  <Icon name="play" size={13} /><span>{q.title}</span>{q.todayMs ? <b className="num">{formatDurationShort(q.todayMs)}</b> : <i>—</i>}
                </button>
              ))}
            </div>
          ) : null}
          {err ? <div style={{ color: 'var(--c-danger)', fontSize: 13 }}>{err}</div> : null}
          {site.list.length ? (
            <div className={s.queue}>
              <div className={s.qHead}>Today on this job · {site.list.filter((t) => t.status === 'done').length} of {site.list.length} done</div>
              {[...site.list].sort((a, b) => (b.id === activeTaskId) - (a.id === activeTaskId) || (a.status === 'done') - (b.status === 'done')).slice(0, 6).map((t) => {
                const late = t.status !== 'done' && t.scheduledDate && t.scheduledDate < today;
                return (
                  <button key={t.id} type="button" className={s.qRow} onClick={() => details.open(t.id)}>
                    <Icon name={t.status === 'done' ? 'check-circle' : t.id === activeTaskId ? 'play-circle' : 'circle'} size={14} style={{ color: t.status === 'done' ? TONE.met : t.id === activeTaskId ? neon[1] : 'var(--c-text-faint)', flex: 'none' }} />
                    <span className={s.qTitle} style={t.status === 'done' ? { textDecoration: 'line-through', color: 'var(--c-text-faint)' } : null}>{t.title}</span>
                    <span className="faint num" style={late ? { color: TONE.close } : t.id === activeTaskId ? { color: neon[1] } : null}>{t.id === activeTaskId ? 'running' : late ? `overdue · ${shortDay(t.scheduledDate)}` : t.estimatedMinutes ? `~${formatDurationShort(t.estimatedMinutes * 60000)}` : ''}</span>
                  </button>
                );
              })}
              {site.list.length > 6 ? <div className={s.qMore}>+{site.list.length - 6} more on today’s list</div> : null}
            </div>
          ) : null}
        </>
      ) : e.jobs.length ? (
        <div className={f.jobs}>
          {e.jobs.slice(0, maxJobs).map(({ project: pj, n, overdue, next, pct, address }, i) => (
            <div key={pj.id} className={f.job} style={{ '--jc': pj.color || neon[1] }}>
              <div className={f.jobTop}>
                <button type="button" className={f.jobName} onClick={() => navigate(`/projects/${pj.id}`)}>{pj.icon ? `${pj.icon} ` : ''}{pj.name}</button>
                <button type="button" className={f.start} data-primary={i === 0 || undefined} onClick={() => clockIn(pj.id)} aria-label={`Start ${pj.name}`}><Icon name="play" size={13} />START</button>
              </div>
              <div className={f.jobMeta}>
                <span style={overdue ? { color: TONE.close } : null}>{n} task{n === 1 ? '' : 's'} today{overdue ? ` · ${overdue} overdue` : ''}</span>
                {address ? <span><Icon name="map-pin" size={12} />{address}</span> : null}
              </div>
              {pct != null ? <div className={f.jobPct}><div className={f.track} style={{ height: 6 }}><span style={{ width: `${pct}%`, background: neon[1] }} /></div><b className="num">{pct}%</b></div> : null}
              {next ? <button type="button" className={f.jobNext} onClick={() => details.open(next.id)}><span>NEXT</span>{next.title}</button> : null}
            </div>
          ))}
        </div>
      ) : (
        <div className={s.heroEmpty}>No Field work on today. <button className={s.link} onClick={() => navigate('/plan')}>Plan the day →</button></div>
      )}
    </section>
  );
}

/** Field hours by job, the last 14 days — the All Home's "Last 14 days" with Field's entries. */
function HoursCard({ x }) {
  const series = x.hours.series;
  const colors = useMemo(() => distinctColors(series.filter((t) => t.id !== 'other').map((t) => t.id), (id) => series.find((t) => t.id === id)?.color), [series]);
  const colorOf = (id) => (id === 'other' ? OTHER : colors.get(id) || OTHER);
  const bars = x.hours.perDay.map((day) => ({
    key: day.date, label: String(new Date(`${day.date}T12:00:00`).getDate()), tip: shortDay(day.date),
    segments: day.parts.filter((pt) => pt.ms > 0).map((pt) => ({ id: pt.id, label: series.find((t) => t.id === pt.id)?.name || 'Other', value: pt.ms / H, color: colorOf(pt.id) })),
  }));
  const target = x.hours.dayTarget;
  return (
    <section className={s.card}>
      <div className={s.cardHead}><h2>Last {x.hours.perDay.length} days</h2><span className={s.cardSub}>Field hours by job · dashed line = {target} h (the Hours worked {x.hours.goalFrom === 'preset' ? 'preset' : 'goal'} ÷ 5){x.hours.liveMs ? ' · counting the running timer' : ''}</span></div>
      <StackedBars data={bars} height={230} target={target} format={(v) => (v >= 1 ? `${v.toFixed(v >= 10 ? 0 : 1)}h` : `${Math.round(v * 60)}m`)} />
      <Legend items={series.map((t) => ({ id: t.id, label: `${t.name} · ${hrs(t.ms)}`, color: colorOf(t.id) }))} />
      {!series.length ? <div className={d.quiet}>No Field hours in {x.hours.perDay.length} days yet.</div> : null}
    </section>
  );
}

/** Field work vs other work (Lj, Oct 11: setup, runs, travel are Field work — "if I'm doing an invoice, that's overhead"):
 *  today and this week, segmented — Field's own three ways, then other work — and the share each day. */
function ProductiveCard({ x, neon }) {
  const c = KIND_COLOR(neon);
  const parts = (list) => ['Job tasks', 'Setup + cleanup', 'Runs + travel', 'Other quick tasks', 'Other work'].map((k) => ({ id: k, label: k, value: list.find((pt) => pt.id === k)?.ms || 0, color: c[k] }))
    .filter((pt) => pt.value > 0 || pt.id !== 'Other quick tasks');
  const pr = x.productive;
  const tone = (v) => (v == null ? undefined : v >= pr.target ? TONE.met : v >= pr.target * 0.9 ? TONE.close : TONE.behind);
  return (
    <section className={s.card}>
      <div className={s.cardHead}><h2>Field work vs other work</h2><span className={s.cardSub}>Field hours (setup, cleanup, runs and travel are Field work) ÷ every hour worked · target {pr.target}%</span></div>
      <div className={f.splitHead}><span>Today</span><b style={{ color: tone(pr.today.share) }}>{pr.today.share == null ? '—' : `${pr.today.share}%`}</b></div>
      {pr.today.parts.length ? <SplitBar parts={parts(pr.today.parts).filter((pt) => pt.value > 0)} format={formatDurationShort} /> : <div className={d.ghost} />}
      <div className={f.splitHead} style={{ marginTop: 14 }}><span>This week</span><b style={{ color: tone(pr.week.share) }}>{pr.week.share == null ? '—' : `${pr.week.share}%`}</b></div>
      <SplitBar parts={parts(pr.week.parts)} format={formatDurationShort} />
      <div className={d.sub}>Share each day · {x.hours.perDay.length} days</div>
      <Sparkline points={pr.days.map((pt) => ({ key: pt.date, label: shortDay(pt.date), value: pt.value || 0 }))} format={(v) => `${v}%`} color={neon[1]} height={40} />
      <Counted counted={pr.counted} leftOut={pr.leftOut} />
    </section>
  );
}

/** Earned per hour: 8 weeks against the target (dashed), then each job this week. */
function EarnedCard({ x }) {
  const e = x.earned;
  const bars = e.weeks.map((w) => ({ key: w.from, label: String(new Date(`${w.from}T12:00:00`).getDate()), tip: weekLabel(w.from), segments: [{ id: 'e', label: 'Earned per hour', value: w.value || 0, color: w.value == null ? OTHER : w.value >= (e.target || 0) ? TONE.met : 'var(--c-gold)' }] }));
  const jobs = e.jobs.filter((j) => j.ms > 0).slice(0, 5);
  return (
    <section className={s.card}>
      <div className={s.cardHead}><h2>Earned per hour</h2><span className={s.cardSub}>8 weeks · dashed = {perH(e.target)}, {targetSource(e.targetFrom, e.targetPct)}</span></div>
      <div className={s.bigRow}>
        <div><div className={s.big} style={{ color: e.value == null ? undefined : e.value >= e.target ? TONE.met : TONE.close }}>{perH(e.value)}</div><div className={s.bigSub}>{x.window?.label || 'this week'}</div></div>
        <div><div className={s.big}>{moneyShort(e.earned)}</div><div className={s.bigSub}>earned · {hrs(e.ms)} on site</div></div>
      </div>
      <div style={{ marginTop: 10 }}><StackedBars data={bars} height={140} target={e.target} format={(v) => `$${Math.round(v)}`} /></div>
      {jobs.length ? (
        <div className={f.jobRows}>
          {jobs.map((j) => {
            const goal = j.target ?? e.target;   // each job against ITS target (v0.1.258)
            const tone = j.perHour == null ? 'var(--c-text-faint)' : goal != null && j.perHour >= goal ? TONE.met : TONE.close;
            const scale = Math.max((e.target || 0) * 1.5, ...jobs.map((x) => Math.max(x.perHour || 0, (x.target || 0) * 1.2)), 1);
            return (
              <div key={j.projectId} className={f.jobRow} title={`${j.project.name}: ${moneyShort(j.earned)} ÷ ${hrs(j.ms)}`}>
                <span className={f.bTop}><span className={f.ell}>{j.project.name}</span><b style={{ color: tone }}>{perH(j.perHour)}</b></span>
                <span className={f.bTrack}>
                  <span className={f.bAct} style={{ width: `${Math.min(100, ((j.perHour || 0) / scale) * 100)}%`, background: tone }} />
                  {goal != null ? <span className={f.bEst} style={{ left: `calc(${(goal / scale) * 100}% - 1px)` }} /> : null}
                </span>
                <span className={f.bSub}><span>{hrs(j.ms)} on site · {moneyShort(j.earned)} earned</span><span>{j.kind === 'none' ? 'internal · no target' : `vs ${perH(j.target)}${j.targetFrom ? ` · ${targetFromText(j.targetFrom, j.labourPct)}` : ''}`}</span></span>
              </div>
            );
          })}
        </div>
      ) : <div className={d.quiet}>No Field hours on a job {x.window?.label || 'this week'} yet.</div>}
      <Counted counted={e.counted} leftOut={e.leftOut} />
    </section>
  );
}

/** Efficiency: a bullet per task finished this week — the estimate (grey) and the actual (green under, red over). */
function EfficiencyCard({ x, onOpen }) {
  const ef = x.efficiency;
  const list = ef.tasks.slice(0, 6);
  const max = Math.max(1, ...list.map((t) => Math.max(t.estMs, t.actualMs)));
  const worst = new Set(ef.worst.map((t) => t.task.id));
  return (
    <section className={s.card}>
      <div className={s.cardHead}><h2>Efficiency</h2><span className={s.cardSub}>estimate ÷ actual on tasks finished {x.window?.label || 'this week'} · bar = actual, mark = estimate</span></div>
      <div className={s.bigRow}>
        <div><div className={s.big} style={{ color: ef.value == null ? undefined : ef.value >= ef.target ? TONE.met : TONE.close }}>{ef.value == null ? '—' : `${ef.value}%`}</div><div className={s.bigSub}>{x.window?.label || 'this week'} · target {ef.target}%</div></div>
        <div style={{ flex: 1, minWidth: 0 }}><Sparkline points={ef.weeks.map((w) => ({ key: w.from, label: weekLabel(w.from), value: w.value || 0 }))} format={(v) => `${v}%`} color="var(--c-success)" height={44} /></div>
      </div>
      <div className={f.bullets}>
        {list.map((t) => {
          const over = t.actualMs > t.estMs;
          return (
            <button key={t.task.id} type="button" className={f.bullet} onClick={() => onOpen(t.task.id)} title={`${t.task.title}: ${hrs(t.estMs)} estimated, ${hrs(t.actualMs)} actual`}>
              <span className={f.bTop}><span className={f.ell} style={worst.has(t.task.id) ? { color: 'var(--c-text)' } : null}>{t.task.title}</span><b style={{ color: over ? TONE.over : TONE.met }}>{t.pct}%</b></span>
              <span className={f.bTrack}>
                <span className={f.bAct} style={{ width: `${(t.actualMs / max) * 100}%`, background: over ? TONE.over : TONE.met }} />
                <span className={f.bEst} style={{ left: `calc(${(t.estMs / max) * 100}% - 1px)` }} />
              </span>
              <span className={f.bSub}>{hrs(t.actualMs)} actual · {hrs(t.estMs)} est{t.project ? ` · ${t.project.name}` : ''}</span>
            </button>
          );
        })}
        {!list.length ? <div className={d.quiet}>No task with an estimate finished {x.window?.label || 'this week'} yet.</div> : null}
      </div>
      {ef.worst.length ? <div className={f.worst}>Slowest: {ef.worst.map((t) => `${t.task.title} (${t.pct}%)`).join(' · ')}</div> : null}
      <Counted counted={ef.counted} leftOut={ef.leftOut} />
    </section>
  );
}

/** The photos this week: before / progress / after, a strip of thumbnails, each day's count; the strip opens a gallery. */
function PhotosCard({ x, neon, onOpen }) {
  const ph = x.photos;
  const [view, setView] = useState(null);
  const shown = ph.list.filter((pt) => isRemoteUri(pt.uri));
  useEffect(() => {
    if (view == null) return undefined;
    const k = (e) => { if (e.key === 'Escape') setView(null); if (e.key === 'ArrowRight') setView((i) => Math.min(shown.length - 1, i + 1)); if (e.key === 'ArrowLeft') setView((i) => Math.max(0, i - 1)); };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, [view, shown.length]);
  const cur = view != null ? shown[view] : null;
  return (
    <section className={s.card}>
      <div className={s.cardHead}><h2>Photos {x.window?.label || 'this week'}</h2><span className={s.cardSub}>on Field tasks, by the day taken · goal {ph.target} a week</span></div>
      <div className={s.bigRow}>
        <div><div className={s.big}>{ph.n}</div><div className={s.bigSub}>photos</div></div>
        {['before', 'progress', 'after'].map((k) => <div key={k}><div className={s.big} style={{ color: ph.counts[k] ? undefined : 'var(--c-text-faint)' }}>{ph.counts[k]}</div><div className={s.bigSub}>{PHOTO_TAG_LABEL[k].toLowerCase()}</div></div>)}
      </div>
      <div className={f.strip}>
        {ph.list.slice(0, 8).map((pt) => {
          const i = shown.indexOf(pt);
          return isRemoteUri(pt.uri)
            ? <button key={pt.uri} type="button" className={f.thumb} onClick={() => setView(i)} title={`${pt.taskTitle} · ${PHOTO_TAG_LABEL[pt.tag]}`}><img src={pt.uri} alt={pt.caption || pt.taskTitle} loading="lazy" /><span data-t={pt.tag}>{PHOTO_TAG_LABEL[pt.tag]}</span></button>
            : <button key={pt.uri} type="button" className={f.thumb} data-local onClick={() => onOpen(pt.taskId)} title="Still on the phone that took it — shows here once uploaded"><Icon name="image" size={18} /><span data-t={pt.tag}>{PHOTO_TAG_LABEL[pt.tag]}</span></button>;
        })}
        {!ph.list.length ? <div className={d.quiet} style={{ gridColumn: '1 / -1' }}>No photos on Field tasks {x.window?.label || 'this week'} yet.</div> : null}
      </div>
      <div className={d.sub}>Each day</div>
      <MiniBars points={ph.days.map((dd) => ({ key: dd.date, label: shortDay(dd.date), value: dd.n, future: dd.future }))} color={neon[1]} format={(v) => `${v} photos`} />
      <div className={f.axis}>{ph.days.map((dd) => <span key={dd.date}>{dayLetter(dd.date)}</span>)}</div>
      {shown.length ? <button type="button" className={s.more} onClick={() => setView(0)}>Gallery · {shown.length} <Icon name="chevron-right" size={13} /></button> : null}
      {cur ? (
        <div className={f.scrim} onMouseDown={(e) => { if (e.target === e.currentTarget) setView(null); }} role="dialog" aria-modal="true" aria-label="Photos this week">
          <img src={cur.uri} alt={cur.caption || cur.taskTitle} className={f.full} />
          <div className={f.lbBar}>
            <button type="button" className={p.iconBtn} disabled={view === 0} onClick={() => setView(view - 1)} aria-label="Previous"><Icon name="chevron-left" size={18} /></button>
            <span>{view + 1} of {shown.length} · {PHOTO_TAG_LABEL[cur.tag]} · {cur.taskTitle}{cur.caption ? ` · ${cur.caption}` : ''}</span>
            <button type="button" className={f.lbLink} onClick={() => { setView(null); onOpen(cur.taskId); }}>Open task</button>
            <button type="button" className={p.iconBtn} disabled={view === shown.length - 1} onClick={() => setView(view + 1)} aria-label="Next"><Icon name="chevron-right" size={18} /></button>
            <button type="button" className={p.iconBtn} onClick={() => setView(null)} aria-label="Close"><Icon name="x" size={18} /></button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/** Receipts snapped this week ($ and count; the ones on no job → Needs you) and material runs with their minutes. */
function ReceiptsRunsCard({ x, onOpen }) {
  const r = x.receipts; const ru = x.runs;
  const overRuns = ru.limit != null && ru.n > ru.limit;
  return (
    <section className={s.card}>
      <div className={s.cardHead}><h2>Receipts + store runs</h2><span className={s.cardSub}>{x.window?.label || 'this week'} · runs = stretches of 2 min or more on a material run · limit {ru.limit}</span></div>
      <div className={s.bigRow} style={{ flexWrap: 'wrap', rowGap: 10, columnGap: 22 }}>
        <div><div className={s.big}>{moneyShort(r.week.total)}</div><div className={s.bigSub}>{r.week.n} receipt{r.week.n === 1 ? '' : 's'}</div></div>
        <button type="button" className={f.bigBtn} disabled={!r.noJob.n} onClick={() => document.getElementById('field-needs')?.scrollIntoView({ behavior: 'smooth' })}>
          <div className={s.big} style={{ color: r.noJob.n ? TONE.close : 'var(--c-text-faint)' }}>{r.noJob.n}</div><div className={s.bigSub}>on no job{r.noJob.n ? ` · ${moneyShort(r.noJob.total)} →` : ''}</div>
        </button>
        <div><div className={s.big} style={{ color: overRuns ? TONE.over : undefined }}>{ru.n}</div><div className={s.bigSub}>runs · {formatDurationShort(ru.ms).replace(' ', '')}</div></div>
      </div>
      <div className={d.sub}>Receipts each day ($)</div>
      <MiniBars points={r.days.map((dd) => ({ key: dd.date, label: shortDay(dd.date), value: dd.total }))} color="var(--c-gold)" format={(v) => moneyShort(v)} height={30} />
      <div className={d.sub}>Material runs</div>
      {ru.rows.slice(0, 4).map((row, i) => (
        <button key={`${row.taskId}${i}`} type="button" className={d.row} onClick={() => onOpen(row.taskId)}>
          <Icon name="truck" size={14} style={{ color: SERIES[1], flex: 'none' }} /><span className={d.rowText}>{row.project?.name || 'No job'} · {shortDay(row.date)}</span>
          <span className={d.age}>{formatDurationShort(row.ms)}</span>
        </button>
      ))}
      {!ru.rows.length ? <div className={d.quiet}>No material runs {x.window?.label || 'this week'}.</div> : null}
    </section>
  );
}

/** Callbacks this month vs the limit (6 months of bars); margin per job finished (90 days) vs the target. */
function CallbacksMarginCard({ x, onOpen, onProject }) {
  const cb = x.callbacks; const m = x.margins;
  const over = cb.limit != null && cb.n > cb.limit;
  return (
    <section className={s.card}>
      <div className={s.cardHead}><h2>Callbacks + job margin</h2><span className={s.cardSub}>#callback finished this month · margin on jobs finished in 90 days vs {m.target}%</span></div>
      <div className={s.bigRow}>
        <div><div className={s.big} style={{ color: over ? TONE.over : TONE.met }}>{cb.n}</div><div className={s.bigSub}>callbacks · limit {cb.limit}</div></div>
        <div><div className={s.big} style={{ color: m.month == null ? 'var(--c-text-faint)' : m.month >= m.target ? TONE.met : TONE.close }}>{m.month == null ? '—' : `${m.month}%`}</div><div className={s.bigSub}>margin this month</div></div>
      </div>
      <MiniBars points={cb.months.map((mm) => ({ key: mm.from, label: new Date(`${mm.from}T12:00:00`).toLocaleDateString(undefined, { month: 'short' }), value: mm.n }))} color={TONE.over} format={(v) => `${v} callbacks`} height={28} />
      <div className={f.axis}>{cb.months.map((mm) => <span key={mm.from}>{new Date(`${mm.from}T12:00:00`).toLocaleDateString(undefined, { month: 'short' })}</span>)}</div>
      {cb.rows.slice(0, 2).map((row) => <button key={row.task.id} type="button" className={d.row} onClick={() => onOpen(row.task.id)}><Icon name="rotate-ccw" size={14} style={{ color: TONE.over, flex: 'none' }} /><span className={d.rowText}>{row.task.title}</span><span className={d.age}>{shortDay(row.date)}</span></button>)}
      <div className={d.sub}>Margin per finished job</div>
      {m.jobs.slice(0, 4).map((j) => (
        <button key={j.project.id} type="button" className={f.marginRow} onClick={() => onProject(j.project.id)} title={`${j.project.name}: ${j.pct}% (${j.paidPct}% if your time were paid out)`}>
          <span className={f.bTop}><span className={f.ell}>{j.project.name}</span><b style={{ color: j.pct >= m.target ? TONE.met : TONE.close }}>{j.pct}%</b></span>
          <span className={f.bTrack}>
            <span className={f.bAct} style={{ width: `${Math.max(0, Math.min(100, j.pct))}%`, background: j.pct >= m.target ? TONE.met : TONE.close }} />
            <span className={f.bEst} style={{ left: `calc(${m.target}% - 1px)` }} />
          </span>
        </button>
      ))}
      {!m.jobs.length ? <div className={d.quiet}>No Field job finished with a contract value in 90 days.</div> : null}
      <Counted counted={m.jobs.length ? m.counted : null} leftOut={m.leftOut} />
    </section>
  );
}

/** One pinned job: contract / spent / margin, hours this week (live) with 7 days, % done, the last photo, the next task. */
function JobCard({ c, color, target, editing, first, last, onRemove, onMove, onOpenProject, onOpenTask }) {
  const pj = c.project;
  const today = localToday();
  const max = Math.max(1, ...c.days.map((dd) => dd.ms));
  return (
    <article className={`${p.app} ${f.jobCard}`} style={{ '--pc': color }}>
      {editing && c.pinned ? <button type="button" className={p.unpin} onClick={onRemove} aria-label={`Remove ${pj.name} from Home`}><Icon name="x" size={13} /></button> : null}
      <div className={p.appHead}>
        <button type="button" className={p.appName} onClick={onOpenProject}>
          <span className={p.appIcon}>{pj.icon || <Icon name="briefcase" size={16} />}</span>
          <span className={p.rowText}>{pj.name}</span>
        </button>
        {editing && c.pinned ? (
          <span className={p.moveBtns}>
            <button type="button" className={p.iconBtn} disabled={first} onClick={() => onMove(-1)} aria-label="Move earlier"><Icon name="chevron-left" size={15} /></button>
            <button type="button" className={p.iconBtn} disabled={last} onClick={() => onMove(1)} aria-label="Move later"><Icon name="chevron-right" size={15} /></button>
          </span>
        ) : c.photo && isRemoteUri(c.photo.uri) ? <img className={f.jobThumb} src={c.photo.uri} alt="Last photo" loading="lazy" /> : c.photo ? <span className={f.jobThumb} data-local title="Last photo — still on the phone"><Icon name="image" size={14} /></span> : null}
      </div>
      <div className={p.figs} style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
        <div className={p.fig}><b>{c.contract ? moneyShort(c.contract) : c.rate ? `$${c.rate}/h` : '—'}</b><span>{c.contract ? 'contract' : c.rate ? 'T&M rate' : 'no contract'}</span></div>
        <div className={p.fig}><b>{c.notLinked ? 'Not linked' : c.spent == null ? '—' : moneyShort(c.spent)}</b><span>spent</span></div>
        <div className={p.fig}><b style={{ color: c.marginPct == null ? undefined : c.marginPct >= target ? TONE.met : c.marginPct >= 0 ? TONE.close : TONE.over }}>{c.marginPct == null ? '—' : `${c.marginPct}%`}</b><span title="the job cost screen's margin (your own unpaid time not costed)">margin</span></div>
      </div>
      <div className={p.appTime}>
        <div>
          <b className={p.appHours}>{hrs(c.weekMs)}</b><span className={p.hintInline}> this week</span>
          {c.running ? <button type="button" className={p.running} onClick={() => onOpenTask(c.running.id)}><span className={p.pulse} /><span className={p.rowText}>{c.running.title}</span></button> : null}
        </div>
        <svg viewBox="0 0 70 30" className={p.appSpark} role="img" aria-label={`Hours, last 7 days: ${c.days.map((dd) => `${shortDay(dd.date)} ${hrs(dd.ms)}`).join(', ')}`}>
          {c.days.map((dd, i) => { const h = dd.ms ? Math.max(3, (dd.ms / max) * 28) : 2; return <rect key={dd.date} x={i * 10} y={30 - h} width={7} height={h} rx={1.5} fill={dd.ms ? color : 'var(--c-border-strong)'} fillOpacity={dd.ms ? (i === 6 ? 1 : 0.6) : 0.6}><title>{`${shortDay(dd.date)}: ${hrs(dd.ms)}`}</title></rect>; })}
        </svg>
      </div>
      <div className={f.jobPct}>
        <div className={f.track} style={{ height: 6 }}><span style={{ width: `${c.donePct || 0}%`, background: color }} /></div>
        <b className="num">{c.donePct == null ? '—' : `${c.donePct}%`}</b>
      </div>
      <div className={f.jobFoot}>
        <span>{c.doneBy === 'estimate' ? 'of the estimate done' : `${c.doneN} of ${c.totalN} tasks done`}</span>
        {c.receipts.n ? <span><Icon name="file-text" size={12} />{c.receipts.n} receipt{c.receipts.n === 1 ? '' : 's'} · {moneyShort(c.receipts.total)}</span> : null}
        {c.photos ? <span><Icon name="camera" size={12} />{c.photos}</span> : null}
        {c.yourTime ? <span title="your hours at your shadow rate, not in spent">{moneyShort(c.yourTime)} your time</span> : null}
      </div>
      <button type="button" className={p.next} disabled={!c.next} onClick={() => c.next && onOpenTask(c.next.task.id)}>
        <span className={p.nextLabel} style={c.next?.overdue ? { color: TONE.over } : null}>{c.next ? (c.next.overdue ? `OVERDUE · ${shortDay(c.next.date).toUpperCase()}` : c.next.date === today ? 'NEXT · TODAY' : c.next.date ? `NEXT · ${shortDay(c.next.date).toUpperCase()}` : 'NEXT · NOT SCHEDULED') : 'NEXT'}</span>
        <span className={p.rowText}>{c.next ? c.next.task.title : 'Nothing open'}</span>
      </button>
    </article>
  );
}

/** The jobs pinned to Field's Home — the same pin list (homeProjectIds) as Dev's apps, Field's job cards. */
function JobsBoard({ dept, jobs, x }) {
  const navigate = useNavigate();
  const details = useTaskModal();
  const [editing, setEditing] = useState(false);
  const [picking, setPicking] = useState(false);
  const { cards, choices, pin, unpin, move } = jobs;
  const colors = useMemo(() => distinctColors(cards.map((c) => c.project.id), (id) => cards.find((c) => c.project.id === id)?.project.color), [cards]);
  return (
    <section className={p.apps}>
      <div className={p.secHead}>
        <div><h2>Jobs</h2><span className={p.secSub}>last worked first, then your pins · contract, spent, margin · hours this week (live){x.receipts.noJob.n ? '' : ''}</span></div>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {x.receipts.noJob.n ? <button type="button" className={f.chip} onClick={() => document.getElementById('field-needs')?.scrollIntoView({ behavior: 'smooth' })}><Icon name="file-text" size={13} />{x.receipts.noJob.n} receipt{x.receipts.noJob.n === 1 ? '' : 's'} on no job</button> : null}
          {cards.length ? <button type="button" className={p.textBtn} onClick={() => setEditing(!editing)} aria-pressed={editing}>{editing ? 'Done' : 'Edit'}</button> : null}
        </span>
      </div>
      <div className={p.appGrid}>
        {cards.map((c, i) => (
          <JobCard key={c.project.id} c={c} color={colors.get(c.project.id) || OTHER} target={x.margins.target} editing={editing} first={i === 0} last={i === cards.length - 1}
            onRemove={() => unpin(c.project.id)} onMove={(dir) => move(c.project.id, dir)} onOpenProject={() => navigate(`/projects/${c.project.id}`)} onOpenTask={(id) => details.open(id)} />
        ))}
        <button type="button" className={p.addApp} onClick={() => setPicking(true)}>
          <span className={p.addIcon}><Icon name="plus" size={18} /></span>
          <b>Add a job</b>
          <span className={p.hint}>{cards.length ? 'Pin another job to this Home' : 'Pin the jobs you’re running — money, hours and what’s next'}</span>
        </button>
      </div>
      {picking ? <Picker choices={choices} onClose={() => setPicking(false)} onPick={async (id) => { await pin(id); setPicking(false); }} /> : null}
    </section>
  );
}

const RANGE_LABEL = { day: 'Day', week: 'Week', month: 'Month', custom: 'Custom' };
const PREV_LABEL = { day: 'yesterday', week: 'last week', month: 'last month', custom: 'before' };
const WIDE_AT = 1500;   // three columns from here (the page, not the window: the tool panel takes room)
const SCOPE_ROWS = [['included', 'In the quote'], ['extra', 'Extras'], ['missed', 'Missed, ours'], ['overhead', 'Overhead'], ['unsorted', 'Not sorted'], ['none', 'No quote']];

/** Day, Week, Month, Custom at the top of the page; every card follows, the dates always showing. */
function RangeBar({ range, setRange, custom, setCustom, label }) {
  const today = localToday();
  return (
    <div className={f.rangeBar}>
      {range === 'custom' ? (
        <span className={f.rangeDates}>
          <input type="date" aria-label="From" className="input" max={custom?.to || today} value={custom?.from || ''} onChange={(ev) => setCustom({ ...custom, from: ev.target.value })} />
          <input type="date" aria-label="To" className="input" min={custom?.from || undefined} value={custom?.to || ''} onChange={(ev) => setCustom({ ...custom, to: ev.target.value })} />
        </span>
      ) : <span className={f.rangeLabel}>{label}</span>}
      <div className={f.rangeSwitch} role="tablist" aria-label="Date range">
        {FIELD_RANGES.map((r) => <button key={r} type="button" role="tab" aria-selected={range === r} data-on={range === r || undefined} onClick={() => setRange(r)}>{RANGE_LABEL[r]}</button>)}
      </div>
    </div>
  );
}

/** Where the window's Field hours went, by each task's scope; the quoted hours left under it. */
function HoursWentCard({ x }) {
  const t = x.time;
  const rows = SCOPE_ROWS.filter(([k]) => t.scope[k] > 0);
  return (
    <section className={s.card}>
      <div className={s.cardHead}><h2>Where the hours went</h2></div>
      {rows.map(([k, l]) => <div key={k} className={d.row} style={{ cursor: 'default' }}><span className={d.rowText} style={k === 'unsorted' ? { color: TONE.close } : null}>{l}</span><span className={f.num}>{hrs(t.scope[k])}</span></div>)}
      {t.workLeft.ms > 0 ? <div className={d.row} style={{ cursor: 'default' }} title={t.workLeft.jobs.map((j) => `${j.project.name}: ${hrs(j.ms)}`).join(' · ')}><span className={d.rowText}>Work left, quoted</span><span className={f.num}>{hrs(t.workLeft.ms)}</span></div> : null}
      {!rows.length && !t.workLeft.ms ? <div className={d.quiet}>No hours {x.window.label}.</div> : null}
    </section>
  );
}

/** The second row of tiles: travel, breaks, fuel, pickups, each against the stretch before it. */
function TimeTiles({ x, neon }) {
  const t = x.time;
  const was = PREV_LABEL[x.range] || 'before';
  const pts = (list, div = 60000) => list.days.map((pt) => ({ key: pt.date, label: shortDay(pt.date), value: pt.value / div }));
  const mins = (v) => formatDurationShort(v * 60000);
  return (
    <div className={f.tiles}>
      <Kpi label="Travel time" value={hrs(t.travel.ms)} sub={`${Math.round(t.travel.km)} km · ${hrs(t.travel.prevMs)} ${was}`} points={pts(t.travel)} format={mins} color="#E8690F" to="/trips" />
      <Kpi label="Breaks" value={hrs(t.breaks.ms)} sub={`${t.breaks.n} break${t.breaks.n === 1 ? '' : 's'} · ${hrs(t.breaks.prevMs)} ${was}`} points={pts(t.breaks)} format={mins} color="#A56CE6" to="/time" />
      <Kpi label={t.fuel.estimate ? 'Fuel, GPS estimate' : 'Fuel'} value={moneyShort(Math.round(t.fuel.cost))} sub={`${moneyShort(Math.round(t.fuel.prev))} ${was}`} points={pts(t.fuel, 1)} format={(v) => moneyShort(Math.round(v))} color="#12A6B8" to="/trips" />
      <Kpi label="Pickups" value={hrs(t.pickups.ms)} sub={`${t.pickups.n} task${t.pickups.n === 1 ? '' : 's'} · ${hrs(t.pickups.prevMs)} ${was}`} points={pts(t.pickups)} format={mins} color={neon[1]} to="/time" />
    </div>
  );
}

/** The jobs as one table on a wide landscape screen: a row each, so they all fit without scrolling. */
function JobsTable({ jobs }) {
  const navigate = useNavigate();
  const details = useTaskModal();
  const [picking, setPicking] = useState(false);
  const { cards, choices, pin } = jobs;
  const colors = useMemo(() => distinctColors(cards.map((c) => c.project.id), (id) => cards.find((c) => c.project.id === id)?.project.color), [cards]);
  const today = localToday();
  return (
    <section className={s.card}>
      <div className={s.cardHead}>
        <h2>Jobs</h2>
        <button type="button" className={p.textBtn} style={{ marginLeft: 'auto' }} onClick={() => setPicking(true)}>Add a job</button>
      </div>
      <div className={f.tableBox}>
        <table className={f.jobsTable}>
          <colgroup><col style={{ width: '24%' }} /><col style={{ width: '14%' }} /><col style={{ width: '13%' }} /><col style={{ width: '14%' }} /><col style={{ width: '10%' }} /><col style={{ width: '25%' }} /></colgroup>
          <thead><tr><th>Job</th><th>Done</th><th>7 days</th><th>Spent</th><th>Contract</th><th>Next</th></tr></thead>
          <tbody>
            {cards.map((c) => {
              const color = colors.get(c.project.id) || OTHER;
              const max = Math.max(1, ...c.days.map((dd) => dd.ms));
              return (
                <tr key={c.project.id}>
                  <td><button type="button" className={f.jobCell} title={c.project.name} onClick={() => navigate(`/projects/${c.project.id}`)}><span className="dot" style={{ background: color, flex: 'none' }} /><span className={f.ell}>{c.project.name}</span></button></td>
                  <td className={f.doneCell}>
                    <span className={f.doneTop}><b className="num">{c.doneBy === 'estimate' && c.donePct != null ? `${c.donePct}%` : `${c.doneN} of ${c.totalN}`}</b>{c.aheadDays ? <i style={{ color: c.aheadDays > 0 ? TONE.met : TONE.close }}>{c.aheadDays > 0 ? '▲' : '▼'} {Math.abs(c.aheadDays)} day{Math.abs(c.aheadDays) === 1 ? '' : 's'}</i> : null}</span>
                    <div className={f.track} style={{ height: 6 }}><span style={{ width: `${c.donePct || 0}%`, background: color }} /></div>
                  </td>
                  <td>
                    <span className={f.weekCell}>
                      <b className="num">{hrs(c.weekMs)}</b>
                      <svg viewBox="0 0 70 24" className={f.weekBars} role="img" aria-label={`Hours, last 7 days: ${c.days.map((dd) => `${shortDay(dd.date)} ${hrs(dd.ms)}`).join(', ')}`}>
                        {c.days.map((dd, i) => { const h = dd.ms ? Math.max(3, (dd.ms / max) * 24) : 2; return <rect key={dd.date} x={i * 10} y={24 - h} width={7} height={h} rx={1.5} fill={dd.ms ? color : 'var(--c-border-strong)'}><title>{`${shortDay(dd.date)}: ${hrs(dd.ms)}`}</title></rect>; })}
                      </svg>
                    </span>
                  </td>
                  <td>{c.notLinked ? <span className={f.notLinked}>Not linked</span> : <b className="num">{moneyShort(c.spent)}</b>}{c.yourTime ? <div className={f.cellSub}>{moneyShort(c.yourTime)} your time</div> : null}</td>
                  <td className="num">{c.contract ? moneyShort(c.contract) : c.rate ? `$${c.rate}/h` : '—'}</td>
                  <td>
                    {c.next ? <button type="button" className={f.nextCell} onClick={() => details.open(c.next.task.id)}><span className={f.ell}>{c.next.task.title}</span>{c.next.overdue ? <span className={f.cellSub} style={{ color: TONE.close }}>Late · {shortDay(c.next.date)}</span> : c.next.date && c.next.date !== today ? <span className={f.cellSub}>{shortDay(c.next.date)}</span> : c.next.date ? <span className={f.cellSub}>Today</span> : null}</button> : <span className="faint">Nothing open</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {picking ? <Picker choices={choices} onClose={() => setPicking(false)} onPick={async (id) => { await pin(id); setPicking(false); }} /> : null}
    </section>
  );
}

/** How wide the page really is (the window less the drawer and any open tool panel). */
function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setW(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((es) => setW(es[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

export default function FieldDashboard({ dept, upNext }) {
  const navigate = useNavigate();
  const details = useTaskModal();
  const { view, data, week, signals } = useDeptGoals(dept);
  const rangeKey = `home.range.${dept?.id || 'all'}`;
  const [range, setRange] = useDeviceSetting(rangeKey, 'week', FIELD_RANGES);   // every card follows
  const [customRaw, setCustomRaw] = useDeviceSetting(`${rangeKey}.custom`, '');
  const custom = useMemo(() => { const [from, to] = String(customRaw || '').split('|'); return from || to ? { from: from || '', to: to || '' } : null; }, [customRaw]);
  const { x, jobs } = useFieldDashboard(dept, { range, custom });
  const [rootRef, width] = useWidth();
  const neon = deptNeon(dept);
  if (!view || !x) return null;
  const vs = (a, b) => (a == null || !b ? null : a >= b ? 'up' : 'down');
  const e = x.earned; const ef = x.efficiency; const pr = x.productive;
  const open = (id) => details.open(id);
  const label = x.window.label;
  const hero = <ScoreHero dept={dept} view={view} week={week} data={data} subtitle={weekOf()} pageRange onOpen={(goalId) => navigate(`/department/${dept.id}${goalId ? `?goal=${goalId}` : view.goals.length ? '' : '?add=1'}`)} />;
  const dates = x.window.from === x.window.to ? shortDay(x.window.from) : `${shortDay(x.window.from)} – ${shortDay(x.window.to)}`;
  const bar = <RangeBar range={x.range} setRange={setRange} custom={custom} setCustom={(c) => setCustomRaw(`${c.from || ''}|${c.to || ''}`)} label={dates} />;
  const kpis = (
    <>
      <Kpi label={x.range === 'day' ? 'Hours today' : 'Hours on site'} value={hrs(x.range === 'day' ? x.hours.today : x.hours.inRange)} tone={x.range === 'day' && x.hours.avg ? vs(x.hours.today, x.hours.avg) : null}
        sub={x.range === 'day' ? (x.hours.avg ? `${x.hours.today >= x.hours.avg ? '▲' : '▼'} vs ${hrs(x.hours.avg)} avg${x.hours.liveMs ? ' · live' : ''}` : label) : `${label}${x.hours.liveMs ? ' · live' : ''}`}
        points={x.hours.perDay.map((pt) => ({ key: pt.date, label: shortDay(pt.date), value: pt.ms / H }))} format={(v) => `${v.toFixed(1)}h`} color={neon[1]} to="/time" />
      <Kpi label="Earned per hour" value={perH(e.value)} tone={vs(e.value, e.target)}
        sub={`vs ${perH(e.target)} · ${label}`}
        points={e.days.map((pt) => ({ key: pt.date, label: shortDay(pt.date), value: pt.value || 0 }))} format={(v) => `$${Math.round(v)}/h`} color="var(--c-gold)" onClick={() => document.getElementById('field-earned')?.scrollIntoView({ behavior: 'smooth' })} />
      <Kpi label="Efficiency" value={ef.value == null ? '—' : `${ef.value}%`} tone={vs(ef.value, ef.target)}
        sub={x.kpi.worstTask ? `slowest ${x.kpi.worstTask.pct}% · ${label}` : label}
        points={ef.weeks.map((w) => ({ key: w.from, label: weekLabel(w.from), value: w.value || 0 }))} format={(v) => `${v}%`} color="var(--c-success)" onClick={() => document.getElementById('field-eff')?.scrollIntoView({ behavior: 'smooth' })} />
      <Kpi label="Productive share" value={pr.value == null ? '—' : `${pr.value}%`} tone={vs(pr.value, pr.target)}
        sub={`vs ${pr.target}% · other work ${hrs(pr.week.parts.find((pt) => pt.id === 'Other work')?.ms || 0)}`}
        points={pr.days.map((pt) => ({ key: pt.date, label: shortDay(pt.date), value: pt.value || 0 }))} format={(v) => `${v}%`} color="#3F7FE6" onClick={() => document.getElementById('field-prod')?.scrollIntoView({ behavior: 'smooth' })} />
    </>
  );
  const needs = (
    <section className={s.card} id="field-needs">
      <div className={s.cardHead}><h2>Needs you</h2><span className="faint num">{signals.length}</span></div>
      {signals.slice(0, 8).map((n, i) => (
        <button key={`${n.kind}${i}`} type="button" className={d.row} onClick={() => (n.taskId ? details.open(n.taskId) : n.projectId ? navigate(`/projects/${n.projectId}`) : null)}>
          <Icon name={n.kind === 'overdue' ? 'clock' : n.kind === 'over' ? 'alert-triangle' : n.kind === 'receipt' ? 'file-text' : 'briefcase'} size={14} style={{ color: n.kind === 'overdue' ? TONE.over : n.kind === 'receipt' ? TONE.close : 'var(--c-text-tertiary)', flex: 'none' }} />
          <span className={d.rowText}>{n.title}</span>{n.detail ? <span className={d.age}>{n.detail}</span> : null}
        </button>
      ))}
      {signals.length > 8 ? <div className={d.quiet}>+{signals.length - 8} more</div> : null}
      {!signals.length ? <div className={d.quiet}>Nothing waiting.</div> : null}
    </section>
  );
  if (width >= WIDE_AT) {
    return (
      <div ref={rootRef} style={{ '--a': deptColor(dept) }}>
        <div className={f.wideTop}>{bar}</div>
        <div className={f.wide}>
          <div className={f.wideRail}>{hero}{needs}</div>
          <div className={f.wideMain}>
            <div className={f.tiles}>{kpis}</div>
            <TimeTiles x={x} neon={neon} />
            <JobsTable jobs={jobs} />
            <div className={f.wideCharts}><HoursCard x={x} /><div id="field-earned"><EarnedCard x={x} /></div></div>
          </div>
          <div className={f.wideRail}><OnSite dept={dept} x={x} neon={neon} maxJobs={3} /><HoursWentCard x={x} /><div id="field-photos"><PhotosCard x={x} neon={neon} onOpen={open} /></div></div>
        </div>
        <div className={f.wideBelow}>
          <div id="field-eff"><EfficiencyCard x={x} onOpen={open} /></div>
          <div id="field-prod"><ProductiveCard x={x} neon={neon} /></div>
          <div id="field-receipts"><ReceiptsRunsCard x={x} onOpen={open} /></div>
          <div id="field-callbacks"><CallbacksMarginCard x={x} onOpen={open} onProject={(id) => navigate(`/projects/${id}`)} /></div>
        </div>
        <div className={f.upnext} style={{ marginTop: 14 }}>{upNext}</div>
      </div>
    );
  }
  return (
    <div ref={rootRef} style={{ '--a': deptColor(dept) }}>
      <div className={f.wideTop}>{bar}</div>
      {hero}

      {/* on site now (or today's jobs) beside the KPI tiles — the All Home's top row */}
      <div className={`${s.topGrid} ${f.top}`}>
        <OnSite dept={dept} x={x} neon={neon} />
        <div className={s.kpis}>{kpis}</div>
      </div>
      <div style={{ marginTop: 16 }}><TimeTiles x={x} neon={neon} /></div>

      {/* the 14 days, where the time went, earned per hour */}
      <div className={`${s.chartGrid} ${d.even}`}>
        <HoursCard x={x} />
        <div id="field-prod"><ProductiveCard x={x} neon={neon} /></div>
        <div id="field-earned"><EarnedCard x={x} /></div>
      </div>

      {/* the jobs, last worked first */}
      <JobsBoard dept={dept} jobs={jobs} x={x} />

      {/* where the hours went · efficiency · photos */}
      <div className={`${s.chartGrid} ${d.even}`}>
        <HoursWentCard x={x} />
        <div id="field-eff"><EfficiencyCard x={x} onOpen={open} /></div>
        <div id="field-photos"><PhotosCard x={x} neon={neon} onOpen={open} /></div>
      </div>

      {/* receipts + runs · callbacks + margin · needs you */}
      <div className={`${s.chartGrid} ${d.even}`}>
        <div id="field-receipts"><ReceiptsRunsCard x={x} onOpen={open} /></div>
        <div id="field-callbacks"><CallbacksMarginCard x={x} onOpen={open} onProject={(id) => navigate(`/projects/${id}`)} /></div>
        {needs}
      </div>
      <div className={f.upnext} style={{ marginTop: 16 }}>{upNext}</div>
    </div>
  );
}
